import {
  Interval,
  addDays,
  eachDayOfInterval,
  endOfWeek,
  startOfWeek,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";

import {
  dateFormat,
  getLastMonday,
} from "@/utils/helper-functions/date-time.helper";

interface IFortnightPicker {
  setDateRange: (val: Date[]) => void;
}

// Define the target date as current date
const targetDate = getLastMonday(new Date());

//Check if the fortnight is the same as the current fortnight
export const isCurrentFortnight = (date: Interval<Date>): boolean => {
  const currentFortnight = eachDayOfInterval({
    start: startOfWeek(targetDate, { weekStartsOn: 1 }),
    end: endOfWeek(addDays(targetDate, 7), { weekStartsOn: 1 }),
  });

  return (
    JSON.stringify(currentFortnight) === JSON.stringify(eachDayOfInterval(date))
  );
};

//Return Current Fortnight
export const getCurrentFortnight = (): Interval<Date> => {
  return {
    start: startOfWeek(targetDate, { weekStartsOn: 1 }),
    end: endOfWeek(addDays(targetDate, 7), { weekStartsOn: 1 }),
  };
};

const FortnightPicker = forwardRef(
  ({ setDateRange }: IFortnightPicker, ref) => {
    // Adjust the initial state to start from the first day of the week
    const [week, setWeek] = useState({
      firstDay: startOfWeek(targetDate, { weekStartsOn: 1 }), // Start from the first day of the week
      lastDay: endOfWeek(addDays(targetDate, 7), { weekStartsOn: 1 }), // End one week later
    });

    const handleDateChange = (dir: boolean): void => {
      if (dir) {
        setWeek({
          firstDay: startOfWeek(addDays(week.firstDay, 14), {
            weekStartsOn: 1,
          }),
          lastDay: endOfWeek(addDays(week.lastDay, 14), { weekStartsOn: 1 }),
        });
      } else {
        setWeek({
          firstDay: startOfWeek(addDays(week.firstDay, -14), {
            weekStartsOn: 1,
          }),
          lastDay: endOfWeek(addDays(week.lastDay, -14), { weekStartsOn: 1 }),
        });
      }
    };

    useImperativeHandle(ref, () => ({
      // upon clicking the clear filter this should reset the fortnight picker date too
      reset: (): void =>
        setWeek({
          firstDay: startOfWeek(targetDate, { weekStartsOn: 1 }), // Start from the first day of the week
          lastDay: endOfWeek(addDays(targetDate, 7), { weekStartsOn: 1 }), // End one week later
        }),
    }));

    useEffect(() => {
      setDateRange(
        eachDayOfInterval({
          start: week.firstDay,
          end: week?.lastDay,
        }),
      );
    }, [week]);

    return (
      <div className='flex items-center justify-between h-10 gap-1 px-1 border rounded-md dashed-border min-w-fit'>
        <div className='arrow-container'>
          <ChevronLeft onClick={() => handleDateChange(false)} size={20} />
        </div>

        <p className='text-sm text-wrap'>
          {dateFormat(week.firstDay, "dd MMM yyyy")} -{" "}
          {dateFormat(week.lastDay, "dd MMM yyyy")}
        </p>
        <div className='arrow-container'>
          <ChevronRight onClick={() => handleDateChange(true)} size={20} />
        </div>
      </div>
    );
  },
);

FortnightPicker.displayName = "FortnightPicker";

export default FortnightPicker;
